import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
const types = {
  SANTE: 'Santé',
  EDUCATION: 'Éducation',
  SECOURS: 'Secours',
  SERVICE_ESSENTIEL: 'Service essentiel',
  AUTRE: 'Autre',
};
interface Site {
  id: string;
  code: string;
  nom: string;
  type: keyof typeof types;
  localisation: string;
  equipement_id: string;
  equipement_code: string;
  equipement_nom: string;
  province: string;
  archive: boolean;
  version: number;
}
export function SitesSensibles({
  equipements,
}: {
  equipements: { id: string; code: string; nom: string; statut: string }[];
}) {
  const { contexte } = useSession();
  const client = useQueryClient();
  const [ouvert, setOuvert] = useState(false);
  const [archives, setArchives] = useState(false);
  const [edition, setEdition] = useState<Site | null>(null);
  const [formulaire, setFormulaire] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState('');
  const q = useQuery({
    queryKey: ['sites-sensibles', contexte?.appartenance_active?.id],
    enabled: ouvert,
    queryFn: () => requete<{ elements: Site[]; tronque: boolean }>('/patrimoine/sites-sensibles'),
  });
  return (
    <details onToggle={(e) => setOuvert(e.currentTarget.open)}>
      <summary>Sites sensibles desservis</summary>
      <p>
        Registre interne de votre organisation. Chaque site est lié à un équipement principal
        identifié par vos services. Cette association ne garantit pas l’alimentation réelle du site
        et ne modifie pas la criticité de l’équipement. Aucun site de cette liste n’est publié sur
        le portail citoyen.
      </p>
      {peut(contexte, 'patrimoine', 'creer') && (
        <button
          onClick={() => {
            setEdition(null);
            setFormulaire(true);
          }}
        >
          Ajouter un site desservi
        </button>
      )}
      {(erreur || q.error) && <p role="alert">{erreur || messageErreur(q.error)}</p>}
      {formulaire && (
        <form
          key={edition?.id ?? 'nouveau'}
          style={{ display: 'grid', gap: 12, maxWidth: 720, marginBlock: 16 }}
          onSubmit={async (e) => {
            e.preventDefault();
            const d = new FormData(e.currentTarget);
            setOccupe(true);
            setErreur('');
            try {
              await requete('/patrimoine/sites-sensibles' + (edition ? '/' + edition.id : ''), {
                methode: edition ? 'PUT' : 'POST',
                corps: {
                  code: d.get('code'),
                  nom: d.get('nom'),
                  type: d.get('type'),
                  localisation: d.get('localisation'),
                  equipement_id: d.get('equipement_id'),
                  ...(edition
                    ? {
                        version: edition.version,
                        archive: d.get('archive') === 'on',
                        motif: d.get('motif'),
                      }
                    : {}),
                },
              });
              setFormulaire(false);
              setEdition(null);
              await client.invalidateQueries({ queryKey: ['sites-sensibles'] });
            } catch (err) {
              setErreur(messageErreur(err));
            } finally {
              setOccupe(false);
            }
          }}
        >
          <label>
            Code du site{' '}
            <input
              name="code"
              required
              pattern="[A-Z0-9-]{3,30}"
              readOnly={!!edition}
              defaultValue={edition?.code}
            />
          </label>
          <label>
            Nom du site{' '}
            <input name="nom" required minLength={3} maxLength={150} defaultValue={edition?.nom} />
          </label>
          <label>
            Type de site{' '}
            <select name="type" defaultValue={edition?.type ?? 'SANTE'}>
              {Object.entries(types).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label>
            Localisation du site{' '}
            <input
              name="localisation"
              required
              minLength={3}
              maxLength={250}
              defaultValue={edition?.localisation}
            />
          </label>
          <label>
            Équipement principal du site{' '}
            <select name="equipement_id" required defaultValue={edition?.equipement_id ?? ''}>
              <option value="">Choisir</option>
              {equipements
                .filter((e) => e.statut !== 'DECOMMISSIONED' || e.id === edition?.equipement_id)
                .map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.code} — {e.nom}
                  </option>
                ))}
            </select>
          </label>
          {edition && (
            <>
              <label>
                <input type="checkbox" name="archive" defaultChecked={edition.archive} /> Archiver
                ce site (conserver son historique)
              </label>
              <label>
                Motif de modification du site{' '}
                <textarea name="motif" required minLength={10} maxLength={500} />
              </label>
            </>
          )}
          <button disabled={occupe}>Enregistrer le site</button>
          <button type="button" onClick={() => setFormulaire(false)}>
            Annuler la saisie du site
          </button>
        </form>
      )}
      <label>
        <input type="checkbox" checked={archives} onChange={(e) => setArchives(e.target.checked)} />{' '}
        Afficher aussi les sites archivés
      </label>
      {ouvert && q.isPending && <p role="status">Chargement des sites…</p>}
      {q.data?.tronque && (
        <p role="alert">
          Liste limitée aux 500 premiers sites ; certains dossiers ne sont pas affichés.
        </p>
      )}
      {q.data && !q.data.elements.some((s) => archives || !s.archive) && (
        <p>Aucun site à afficher.</p>
      )}
      {q.data?.elements
        .filter((s) => archives || !s.archive)
        .map((s) => (
          <article key={s.id} style={{ marginBlock: 20 }}>
            <h3>
              {s.code} — {s.nom}
            </h3>
            <p>
              {types[s.type]} · {s.archive ? 'Archivé' : 'Actif'} · {s.province}
            </p>
            <p>{s.localisation}</p>
            <p>
              Équipement principal : {s.equipement_code} — {s.equipement_nom}
            </p>
            {peut(contexte, 'patrimoine', 'modifier') && (
              <button
                onClick={() => {
                  setEdition(s);
                  setFormulaire(true);
                }}
              >
                Modifier ce site
              </button>
            )}
            <HistoriqueSite key={`${s.id}-${s.version}`} id={s.id} />
          </article>
        ))}
    </details>
  );
}
function HistoriqueSite({ id }: { id: string }) {
  const [ouvert, setOuvert] = useState(false);
  const q = useQuery({
    queryKey: ['sites-sensibles', 'historique', id],
    enabled: ouvert,
    queryFn: () =>
      requete<{
        elements: {
          cree_le: string;
          motif: string;
          apres: { nom: string; localisation: string; archive: boolean; version: number };
        }[];
      }>(`/patrimoine/sites-sensibles/${id}/historique`),
  });
  return (
    <details onToggle={(e) => setOuvert(e.currentTarget.open)}>
      <summary>Historique du site</summary>
      {q.error && <p role="alert">{messageErreur(q.error)}</p>}
      <ul>
        {q.data?.elements.map((h, i) => (
          <li key={i}>
            {new Date(h.cree_le).toLocaleString('fr-FR')} — {h.motif} · version {h.apres.version} :{' '}
            {h.apres.nom}, {h.apres.localisation}, {h.apres.archive ? 'archivé' : 'actif'}
          </li>
        ))}
      </ul>
    </details>
  );
}
